import actionTypes from "../actionTypes";
import { paymentService } from "../../../services/paymentService";
import { getUser } from "../../../services/localService";
import logger from "../../../utils/logger";

/**
 * Set payment setups in Redux store
 */
export const setPaymentSetups = paymentSetups => ({
  type: actionTypes.SET_PAYMENT_SETUPS,
  payload: paymentSetups,
});

/**
 * Set payment setup loading state
 */
export const setPaymentSetupLoading = loading => ({
  type: actionTypes.SET_PAYMENT_SETUP_LOADING,
  payload: loading,
});

/**
 * Set payment setup error
 */
export const setPaymentSetupError = error => ({
  type: actionTypes.SET_PAYMENT_SETUP_ERROR,
  payload: error,
});

/**
 * Clear payment setups from Redux store
 */
export const clearPaymentSetups = () => ({
  type: actionTypes.CLEAR_PAYMENT_SETUPS,
});

/**
 * Set payment setup wall checked flag
 */
export const setPaymentSetupWallChecked = checked => ({
  type: actionTypes.SET_PAYMENT_SETUP_WALL_CHECKED,
  payload: checked,
});

/**
 * Fetch payment setups from API and store in Redux
 * This is called from postAuthFlow after login
 */
export const fetchPaymentSetups = () => async dispatch => {
  try {
    dispatch(setPaymentSetupWallChecked(false));
    dispatch(setPaymentSetupLoading(true));

    const user = getUser();
    const organizationId = user?.organizationId || user?.organization?.id;
    const orgTypeCode = user?.organization?.type?.code;

    logger.log("fetchPaymentSetups: Fetching payment setups for org:", {
      organizationId,
      orgTypeCode,
    });

    // Payment setup check is only required for host organizations, excluding specific customer roles
    const userRoleCode = user?.organizationRole?.code || user?.role;
    if (
      !organizationId ||
      (orgTypeCode !== "HOST_PROPERTY_MANAGEMENT" && orgTypeCode !== "HOST_PROPERTY_DISTRIBUTION") ||
      userRoleCode === "ROLE_HOST_CUSTOMER_INDIVIDUAL"
    ) {
      logger.log("fetchPaymentSetups: Skipping - not a host admin/ops role or organization type requiring setup");
      dispatch(setPaymentSetups([])); // Empty array means "not applicable"
      dispatch(setPaymentSetupWallChecked(true));
      dispatch(setPaymentSetupLoading(false));
      return;
    }

    const response = await paymentService.getOrganizationPaymentSetups(organizationId);

    logger.log("fetchPaymentSetups: API response:", response);

    // Handle different response structures
    let dataArray = null;
    if (response && response.data && Array.isArray(response.data)) {
      dataArray = response.data;
    } else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
      dataArray = response.data.data;
    } else if (Array.isArray(response)) {
      dataArray = response;
    }

    logger.log("fetchPaymentSetups: Extracted payment setups:", dataArray);

    dispatch(setPaymentSetups(dataArray || []));
    dispatch(setPaymentSetupWallChecked(true));
    dispatch(setPaymentSetupLoading(false));
  } catch (error) {
    logger.error("fetchPaymentSetups: Error fetching payment setups:", error);

    // Check if it's a 401 authentication error
    const status = error.response?.status;
    const is401 = status === 401;

    let errorMessage = error.message || "Failed to fetch payment setups";
    if (is401) {
      errorMessage = "Authentication failed (401). Your session may have expired.";
      logger.warn("fetchPaymentSetups: 401 error - token may be expired");
    }

    dispatch(setPaymentSetupError(errorMessage));
    dispatch(setPaymentSetupWallChecked(true));
    dispatch(setPaymentSetupLoading(false));
  }
};

/**
 * Refresh payment setups - call this after adding/updating payment setup
 */
export const refreshPaymentSetups = () => async dispatch => {
  logger.log("refreshPaymentSetups: Refreshing payment setup state");
  await dispatch(fetchPaymentSetups());
};
